import React, { useState } from 'react';
import { Ruler, User } from 'lucide-react';
import BodyOverlayView, { CATEGORY_TO_BODY } from './BodyOverlayView';

const REFERENCE_OBJECTS = [
  { name: '₹1 Coin', widthMm: 25, heightMm: 25, shape: 'circle', color: '#C9A967' },
  { name: '₹5 Coin', widthMm: 23, heightMm: 23, shape: 'circle', color: '#A0A0A0' },
  { name: 'Credit Card', widthMm: 85.6, heightMm: 53.98, shape: 'rect', color: '#1a3a5c' },
  { name: 'Matchbox', widthMm: 53, heightMm: 36, shape: 'rect', color: '#8B6914' },
];

const PX_PER_MM = 3;

function RulerSVG({ lengthMm, direction = 'horizontal' }) {
  const totalPx = lengthMm * PX_PER_MM;
  const isH = direction === 'horizontal';
  const width = isH ? totalPx + 2 : 28;
  const height = isH ? 28 : totalPx + 2;

  const ticks = [];
  for (let mm = 0; mm <= lengthMm; mm++) {
    const pos = mm * PX_PER_MM + 1;
    const isCm = mm % 10 === 0;
    const isHalfCm = mm % 5 === 0;
    const tickLen = isCm ? 14 : isHalfCm ? 10 : 5;

    if (isH) {
      ticks.push(
        <line key={mm} x1={pos} y1={0} x2={pos} y2={tickLen}
          stroke="#1a3a5c" strokeWidth={isCm ? 1.2 : 0.6} />
      );
      if (isCm && mm > 0) {
        ticks.push(
          <text key={`t${mm}`} x={pos} y={24} textAnchor="middle"
            fill="#1a3a5c" fontSize="9" fontFamily="serif">{mm / 10}</text>
        );
      }
    } else {
      ticks.push(
        <line key={mm} x1={0} y1={pos} x2={tickLen} y2={pos}
          stroke="#1a3a5c" strokeWidth={isCm ? 1.2 : 0.6} />
      );
      if (isCm && mm > 0) {
        ticks.push(
          <text key={`t${mm}`} x={24} y={pos + 3} textAnchor="middle"
            fill="#1a3a5c" fontSize="9" fontFamily="serif">{mm / 10}</text>
        );
      }
    }
  }

  return (
    <svg width={width} height={height} className="flex-shrink-0">
      {isH ? (
        <line x1={1} y1={0} x2={totalPx + 1} y2={0} stroke="#1a3a5c" strokeWidth="1.5" />
      ) : (
        <line x1={0} y1={1} x2={0} y2={totalPx + 1} stroke="#1a3a5c" strokeWidth="1.5" />
      )}
      {ticks}
    </svg>
  );
}

function ReferenceShape({ obj, scaledW, scaledH }) {
  if (obj.shape === 'circle') {
    const r = scaledW / 2;
    return (
      <svg width={scaledW} height={scaledH}>
        <circle cx={r} cy={r} r={r - 1} fill="none" stroke={obj.color} strokeWidth="2"
          strokeDasharray="4 2" />
        <text x={r} y={r + 3} textAnchor="middle" fill={obj.color} fontSize="8"
          fontFamily="serif">{obj.name}</text>
      </svg>
    );
  }
  return (
    <svg width={scaledW} height={scaledH}>
      <rect x={1} y={1} width={scaledW - 2} height={scaledH - 2} rx={3}
        fill="none" stroke={obj.color} strokeWidth="2" strokeDasharray="4 2" />
      <text x={scaledW / 2} y={scaledH / 2 + 3} textAnchor="middle" fill={obj.color}
        fontSize="8" fontFamily="serif">{obj.name}</text>
    </svg>
  );
}

export default function ProductSizeScale({ widthMm, heightMm, productName, modelCategory, productImage }) {
  const [selectedRef, setSelectedRef] = useState(0);
  const [activeView, setActiveView] = useState('scale');

  if (!widthMm && !heightMm) return null;

  const w = widthMm || 0;
  const h = heightMm || 0;
  const maxDim = Math.max(w, h);
  const hasBodyView = modelCategory && CATEGORY_TO_BODY[modelCategory];

  const rulerMm = Math.ceil(Math.max(maxDim, REFERENCE_OBJECTS[selectedRef].widthMm, REFERENCE_OBJECTS[selectedRef].heightMm) / 10) * 10 + 10;

  const ref = REFERENCE_OBJECTS[selectedRef];
  const refScaledW = ref.widthMm * PX_PER_MM;
  const refScaledH = ref.heightMm * PX_PER_MM;
  const productScaledW = w * PX_PER_MM;
  const productScaledH = h * PX_PER_MM;

  return (
    <div className="border-t border-gray-200 pt-6" data-testid="product-size-scale">
      <h3 className="font-serif text-xl font-semibold text-[#1a3a5c] mb-1 flex items-center gap-2">
        <Ruler className="h-5 w-5 text-[#C9A967]" />
        Size & Scale
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Actual size representation — {w > 0 && <span>{w} mm wide</span>}
        {w > 0 && h > 0 && ' × '}
        {h > 0 && <span>{h} mm tall</span>}
      </p>

      {/* View toggle */}
      {hasBodyView && (
        <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit" data-testid="scale-view-toggle">
          <button
            onClick={() => setActiveView('scale')}
            className={`px-4 py-2 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${
              activeView === 'scale'
                ? 'bg-white text-[#1a3a5c] shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="toggle-scale-view"
          >
            <Ruler className="h-3.5 w-3.5" />
            Ruler & Scale
          </button>
          <button
            onClick={() => setActiveView('body')}
            className={`px-4 py-2 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${
              activeView === 'body'
                ? 'bg-white text-[#1a3a5c] shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="toggle-body-view"
          >
            <User className="h-3.5 w-3.5" />
            View on Body
          </button>
        </div>
      )}

      {activeView === 'scale' ? (
        <React.Fragment>
          {/* Reference selector */}
          <div className="flex flex-wrap gap-2 mb-4">
            {REFERENCE_OBJECTS.map((obj, i) => (
              <button
                key={obj.name}
                onClick={() => setSelectedRef(i)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                  selectedRef === i
                    ? 'bg-[#1a3a5c] text-white border-[#1a3a5c]'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-[#1a3a5c]'
                }`}
                data-testid={`ref-object-${i}`}
              >
                {obj.name}
              </button>
            ))}
          </div>

          {/* Scale visualization */}
          <div className="bg-[#FDFBF7] border border-[#D4AF37]/20 rounded-lg p-5 overflow-x-auto">
            <div className="inline-block min-w-fit">
              <div className="mb-2 ml-[30px]">
                <RulerSVG lengthMm={rulerMm} direction="horizontal" />
                <div className="text-[10px] text-gray-400 mt-0.5 text-center" style={{ width: rulerMm * PX_PER_MM }}>
                  centimetres
                </div>
              </div>

              <div className="flex items-start">
                <div className="mr-2 flex-shrink-0">
                  <RulerSVG lengthMm={rulerMm} direction="vertical" />
                </div>

                <div className="relative flex items-end gap-6 pb-2" style={{ minHeight: Math.max(productScaledH, refScaledH) + 10 }}>
                  {(w > 0 || h > 0) && (
                    <div className="flex flex-col items-center">
                      <div
                        className="border-2 border-[#C9A967] rounded-sm bg-[#C9A967]/10 flex items-center justify-center"
                        style={{
                          width: Math.max(productScaledW, 20),
                          height: Math.max(productScaledH, 20),
                        }}
                        data-testid="product-silhouette"
                      >
                        <span className="text-[9px] text-[#8B7355] font-medium text-center leading-tight px-1">
                          {productName?.split(' ')[0] || 'Product'}
                        </span>
                      </div>
                      <span className="text-[10px] text-[#8B7355] mt-1 whitespace-nowrap">
                        {w > 0 && `${w}mm`}{w > 0 && h > 0 && ' × '}{h > 0 && `${h}mm`}
                      </span>
                    </div>
                  )}

                  <div className="flex flex-col items-center">
                    <ReferenceShape obj={ref} scaledW={refScaledW} scaledH={refScaledH} />
                    <span className="text-[10px] text-gray-500 mt-1 whitespace-nowrap">
                      {ref.widthMm}mm{ref.shape === 'rect' ? ` × ${ref.heightMm}mm` : ''}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-2 italic">
            Sizes shown to scale. Screen display may vary slightly from actual dimensions.
          </p>
        </React.Fragment>
      ) : (
        <div className="bg-[#FDFBF7] border border-[#D4AF37]/20 rounded-lg p-5">
          <BodyOverlayView
            modelCategory={modelCategory}
            widthMm={w}
            heightMm={h}
            productImage={productImage}
            productName={productName}
          />
          <p className="text-xs text-gray-400 mt-3 italic text-center">
            Approximate placement shown to scale. Actual appearance may vary.
          </p>
        </div>
      )}
    </div>
  );
}
